import React, { useEffect, useState } from 'react';

/**
 * Thin progress bar pinned to the top of the viewport, shown while a background refetch is running.
 *
 * Pages call startLoading() / stopLoading() around a refetch instead of blanking to a skeleton, so
 * the current data stays on screen (stale-while-revalidate). Purely presentational — it never
 * touches requests or data.
 *
 * Concurrent refetches are ref-counted: the bar only finishes when the last one does.
 */

let pending = 0;
const listeners = new Set();
const emit = () => listeners.forEach(fn => fn(pending));

export const startLoading = () => { pending += 1; emit(); };
export const stopLoading = () => { pending = Math.max(0, pending - 1); emit(); };

// Wrap a promise so the bar starts/stops even if it rejects. Errors propagate untouched.
export const withLoading = async (promise) => {
  startLoading();
  try { return await promise; }
  finally { stopLoading(); }
};

export default function TopProgressBar() {
  const [active, setActive] = useState(false);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const onChange = (count) => setActive(count > 0);
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, []);

  useEffect(() => {
    if (!active) {
      // Snap to full, then fade out — reads as "finished", not "vanished".
      if (width > 0) {
        setWidth(100);
        const t = setTimeout(() => setWidth(0), 280);
        return () => clearTimeout(t);
      }
      return undefined;
    }
    // Creep toward 90% and wait there; the real completion supplies the last 10%.
    setWidth(12);
    const id = setInterval(() => {
      setWidth(w => (w >= 90 ? w : w + Math.max(0.5, (90 - w) * 0.12)));
    }, 180);
    return () => clearInterval(id);
  }, [active]); // `width` is intentionally excluded — reading it here would restart the creep

  if (width === 0) return null;

  return (
    <div
      className="mc-topbar"
      style={{ width: `${width}%`, opacity: active || width === 100 ? 1 : 0 }}
      role="progressbar"
      aria-hidden="true"
    />
  );
}
